import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { injectTranslate } from '@pegasus/translation';

import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { isSmScreen } from '@client/helpers/screens';
import { Grid, GridRow, GridCol } from '@lux/grid';
import { CategoryTab } from '@shared/types/mylounge';
import { RootState } from '@client/redux/types';

import { ANIMATION_WAIT } from '../constants';
import { Tab } from './Tab';
import * as S from './tabs.styles';
import { useCrossCampaignEntryLargeScreen } from '../../cross-campaign/hooks/use-cross-campaign-entry-large-screen';

interface Props {
    tabs: CategoryTab[];
    onTabClick: (tabIndex: number, timeout: number) => {};
    translate: (message: string, params?: any) => string;
    activeIndex: number;
    deviceWidth: number;
}

const Tabs = ({ tabs, activeIndex, onTabClick, translate, deviceWidth }: Props) => {
    const tabsRef = useRef(null);
    const tabsWrapperRef = useRef(null);
    const { showMenu, hideMenu, isMenuShown, isExpandableTab, CrossCampaignEntryLargeScreen } =
        useCrossCampaignEntryLargeScreen();

    const normalizeTabName = (name: string) => (typeof name === 'string' ? name : '');

    const changeTab = (tabIndex: number) => {
        const timeout = isSmScreen(deviceWidth) ? ANIMATION_WAIT : 0;
        onTabClick(tabIndex, timeout);
    };

    const updateTabsPosition = () => {
        if (!tabsRef.current || !tabs || !tabs.length || !tabs[activeIndex]) return;

        if (!isSmScreen(deviceWidth)) {
            tabsRef.current.parentElement.scrollLeft = 0;
            return;
        }

        const containerWidth = tabsWrapperRef.current.getBoundingClientRect().width;
        const children = Array.from(tabsRef.current.childNodes);
        // @ts-ignore
        const { x, width } = children[activeIndex]?.getBoundingClientRect() || {};
        const difference = containerWidth * 0.5 - width * 0.5 - x;
        tabsRef.current.parentElement.scrollLeft -= difference;
    };

    useEffect(() => {
        updateTabsPosition();
    }, [activeIndex, deviceWidth]);

    return (
        <Grid className="tabs__wrapper">
            <GridRow>
                <GridCol span={12}>
                    {/* @TODO needs aria label translation */}
                    <S.TabsContainer ref={tabsWrapperRef} aria-label="Main">
                        <S.Tabs ref={tabsRef}>
                            {tabs.map((tab, tabIndex) => (
                                <Tab
                                    tab={tab}
                                    key={tab.id}
                                    name={normalizeTabName(tab.name)}
                                    index={tabIndex}
                                    active={tabIndex === activeIndex}
                                    onAction={() => changeTab(tabIndex)}
                                    showMenu={showMenu}
                                    hideMenu={hideMenu}
                                    isMenuShown={isMenuShown}
                                    isExpandableTab={isExpandableTab}
                                    CrossCampaignEntryLargeScreen={CrossCampaignEntryLargeScreen}
                                />
                            ))}
                        </S.Tabs>
                    </S.TabsContainer>
                </GridCol>
            </GridRow>
        </Grid>
    );
};

const mapStateToProps = (state: RootState) => ({
    deviceWidth: getClientDeviceWidth(state),
});

export default connect(mapStateToProps, {})(injectTranslate(Tabs));
